/** Cancel a read's caller even when its RPC transport cannot cancel the request.
 * Do not wrap proving or journal mutations: their cleanup must finish under the
 * wallet lease. A late read can fill a public cache, but cannot resume this caller.
 */
export function withReadCancellation<T>(
  read: () => Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  const cancelled = () =>
    Object.assign(new Error("Read cancelled"), { name: "AbortError" });
  if (signal?.aborted) return Promise.reject(cancelled());
  if (!signal) return read();
  return new Promise<T>((resolve, reject) => {
    const abort = () => {
      signal.removeEventListener("abort", abort);
      reject(cancelled());
    };
    signal.addEventListener("abort", abort, { once: true });
    Promise.resolve()
      .then(() => {
        if (signal.aborted) throw cancelled();
        return read();
      })
      .then(
        (value) => {
          signal.removeEventListener("abort", abort);
          resolve(value);
        },
        (error) => {
          signal.removeEventListener("abort", abort);
          reject(error);
        },
      );
  });
}
